import React, { useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

// 定义消息类型的联合类型
export type MessageType = 'success' | 'error' | 'info' | 'warning';

// 定义单条消息的配置接口
export interface MessageConfig {
  id?: string | number; // 内部使用，自动生成
  type?: MessageType;
  content: ReactNode; // 支持字符串或 React 节点
  duration?: number; // 自动关闭时间，设为 0 则不自动关闭
  closable?: boolean; // 是否显示关闭按钮
  closeText?: string; // 自定义关闭按钮文案
}

// 图标与样式的映射配置
const typeConfig: Record<MessageType, { icon: React.ElementType; color: string; bg: string; border: string }> = {
  success: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
  error: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  warning: { icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
};

// 单条消息组件
interface MessageItemProps {
  message: Required<Pick<MessageConfig, 'id' | 'type' | 'duration' | 'closable'>> & MessageConfig;
  onClose: (id: string | number) => void;
}

const MessageItem = ({ message, onClose }: MessageItemProps) => {
  const { icon: Icon, color, bg, border } = typeConfig[message.type] || typeConfig.info;

  useEffect(() => {
    if (message.duration) {
      const timer = setTimeout(() => {
        onClose(message.id);
      }, message.duration);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  return (
    <div className={`flex items-start p-4 mb-3 rounded-lg shadow-lg border max-w-md ${bg} ${border} animate-slide-in`}>
      <Icon className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${color}`} />
      
      {/* 支持多行文本和 React 节点 */}
      <div className="flex-1 text-sm text-gray-800 whitespace-pre-wrap break-words">
        {message.content}
      </div>

      {/* 自定义关闭按钮逻辑 */}
      {message.closable !== false && (
        <button 
          onClick={() => onClose(message.id)} 
          className="ml-4 text-gray-400 hover:text-gray-600 flex-shrink-0 flex items-center gap-1"
        >
          {message.closeText ? (
            <span className="text-xs">{message.closeText}</span>
          ) : (
            <X size={16} />
          )}
        </button>
      )}
    </div>
  );
};

// 全局 Message 容器
interface MessageContainerProps {
  messages: MessageConfig[];
  removeMessage: (id: string | number) => void;
}

export const MessageContainer = ({ messages, removeMessage }: MessageContainerProps) => {
  return createPortal(
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center">
      {messages.map((msg) => (
        // 确保渲染时 id 存在
        msg.id && <MessageItem key={msg.id} message={msg as any} onClose={removeMessage} />
      ))}
    </div>,
    document.body
  );
};